/**
 * Form controls.
 *
 * These carry the accessibility Carbon was providing, which is the whole reason
 * replacing it is risky rather than cosmetic:
 *
 *   - every control's label is a real `<label for>`, never a floating div;
 *   - `helperText` and `invalidText` are wired through `aria-describedby`, so a
 *     screen reader hears the explanation with the field rather than after it;
 *   - `invalid` sets `aria-invalid`, so the error is announced as an error and
 *     not merely coloured red;
 *   - focus is always visible — the ring is on `:focus-visible`, so it appears
 *     for keyboard users without following every mouse click.
 *
 * Prop names mirror Carbon's (`labelText`, `helperText`, `invalidText`,
 * `hideLabel`, and the `(event, { value })` / `(event, { checked })` change
 * signatures) so the two large forms — CenterForm and the ~70-field
 * EdgeNodeWizard, both fixed earlier — migrate by changing an import.
 */

import { useId } from 'react';
import type { ChangeEvent, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

/* ── shared field chrome ─────────────────────────────────────────────────── */

interface FieldShellProps {
  id: string;
  labelText: ReactNode;
  hideLabel?: boolean;
  helperText?: ReactNode;
  invalid?: boolean;
  invalidText?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Ids for the description elements, or undefined when there is nothing to point at. */
function describedBy(id: string, helper: boolean, error: boolean): string | undefined {
  const ids = [helper ? `${id}-helper` : '', error ? `${id}-error` : ''].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

function FieldShell({
  id,
  labelText,
  hideLabel = false,
  helperText,
  invalid = false,
  invalidText,
  className,
  children,
}: FieldShellProps) {
  const showError = invalid && Boolean(invalidText);
  return (
    <div className={['field', className ?? ''].filter(Boolean).join(' ')}>
      <label className={hideLabel ? 'field__label field__label--hidden' : 'field__label'} htmlFor={id}>
        {labelText}
      </label>
      {children}
      {showError ? (
        <p className="field__error" id={`${id}-error`}>
          {invalidText}
        </p>
      ) : null}
      {helperText ? (
        <p className="field__help" id={`${id}-helper`}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

/* ── text ────────────────────────────────────────────────────────────────── */

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'id'> {
  id?: string;
  labelText: ReactNode;
  hideLabel?: boolean;
  helperText?: ReactNode;
  invalid?: boolean;
  invalidText?: ReactNode;
}

export function TextInput({
  id,
  labelText,
  hideLabel,
  helperText,
  invalid = false,
  invalidText,
  className,
  ...rest
}: TextInputProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <FieldShell
      id={fieldId}
      labelText={labelText}
      hideLabel={hideLabel ?? false}
      helperText={helperText}
      invalid={invalid}
      invalidText={invalidText}
      className={className ?? ''}
    >
      <input
        id={fieldId}
        className={`input${invalid ? ' input--invalid' : ''}`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(fieldId, Boolean(helperText), invalid && Boolean(invalidText))}
        {...rest}
      />
    </FieldShell>
  );
}

export function TextArea({
  id,
  labelText,
  hideLabel,
  helperText,
  invalid = false,
  invalidText,
  rows = 4,
  className,
  ...rest
}: Omit<TextInputProps, 'type'> & { rows?: number }) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <FieldShell
      id={fieldId}
      labelText={labelText}
      hideLabel={hideLabel ?? false}
      helperText={helperText}
      invalid={invalid}
      invalidText={invalidText}
      className={className ?? ''}
    >
      <textarea
        id={fieldId}
        rows={rows}
        className={`input input--area${invalid ? ' input--invalid' : ''}`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(fieldId, Boolean(helperText), invalid && Boolean(invalidText))}
        {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    </FieldShell>
  );
}

/* ── number ──────────────────────────────────────────────────────────────────
   Carbon's NumberInput calls back with `(event, { value })` where value may be
   a string (the field can be mid-edit and empty). Both the wizard and the
   center form destructure exactly that, so the shape is preserved verbatim. */

export interface NumberInputProps {
  id?: string;
  /** Carbon names this `label`, not `labelText`. Kept, so call sites don't move. */
  label: ReactNode;
  hideLabel?: boolean;
  helperText?: ReactNode;
  invalid?: boolean;
  invalidText?: ReactNode;
  value: number | string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    state: { value: number | string },
  ) => void;
}

export function NumberInput({
  id,
  label,
  hideLabel,
  helperText,
  invalid = false,
  invalidText,
  value,
  min,
  max,
  step,
  disabled,
  placeholder,
  className,
  onChange,
}: NumberInputProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <FieldShell
      id={fieldId}
      labelText={label}
      hideLabel={hideLabel ?? false}
      helperText={helperText}
      invalid={invalid}
      invalidText={invalidText}
      className={className ?? ''}
    >
      <input
        id={fieldId}
        type="number"
        className={`input input--number${invalid ? ' input--invalid' : ''}`}
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled ?? false}
        placeholder={placeholder ?? ''}
        inputMode="numeric"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(fieldId, Boolean(helperText), invalid && Boolean(invalidText))}
        onChange={(event) => {
          const raw = event.target.value;
          onChange?.(event, { value: raw === '' ? '' : Number(raw) });
        }}
      />
    </FieldShell>
  );
}

/* ── select ──────────────────────────────────────────────────────────────── */

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'id'> {
  id?: string;
  labelText: ReactNode;
  hideLabel?: boolean;
  helperText?: ReactNode;
  invalid?: boolean;
  invalidText?: ReactNode;
  children: ReactNode;
}

export function Select({
  id,
  labelText,
  hideLabel,
  helperText,
  invalid = false,
  invalidText,
  className,
  children,
  ...rest
}: SelectProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <FieldShell
      id={fieldId}
      labelText={labelText}
      hideLabel={hideLabel ?? false}
      helperText={helperText}
      invalid={invalid}
      invalidText={invalidText}
      className={className ?? ''}
    >
      <div className="select-wrap">
        <select
          id={fieldId}
          className={`input select${invalid ? ' input--invalid' : ''}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy(
            fieldId,
            Boolean(helperText),
            invalid && Boolean(invalidText),
          )}
          {...rest}
        >
          {children}
        </select>
        <svg className="select-wrap__chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="m5 9 7 7 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </FieldShell>
  );
}

/** Mirrors Carbon's `SelectItem`, which takes `text` rather than children. */
export function SelectItem({
  value,
  text,
  disabled,
}: {
  value: string | number;
  text: string;
  disabled?: boolean;
}) {
  return (
    <option value={value} disabled={disabled ?? false}>
      {text}
    </option>
  );
}

export function SelectItemGroup({ label, children }: { label: string; children: ReactNode }) {
  return <optgroup label={label}>{children}</optgroup>;
}

/* ── checkbox & toggle ───────────────────────────────────────────────────── */

export function Checkbox({
  id,
  labelText,
  checked,
  disabled,
  helperText,
  onChange,
}: {
  id?: string;
  labelText: ReactNode;
  checked: boolean;
  disabled?: boolean;
  helperText?: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>, state: { checked: boolean }) => void;
}) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="check">
      <input
        id={fieldId}
        type="checkbox"
        className="check__box"
        checked={checked}
        disabled={disabled ?? false}
        aria-describedby={helperText ? `${fieldId}-helper` : undefined}
        onChange={(event) => onChange?.(event, { checked: event.target.checked })}
      />
      <label className="check__label" htmlFor={fieldId}>
        {labelText}
      </label>
      {helperText ? (
        <p className="field__help check__help" id={`${fieldId}-helper`}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A switch. `role="switch"` rather than a styled checkbox, because the two are
 * announced differently and this one takes effect immediately.
 */
export function Toggle({
  id,
  labelText,
  toggled,
  disabled,
  onToggle,
}: {
  id?: string;
  labelText: ReactNode;
  toggled: boolean;
  disabled?: boolean;
  onToggle?: (next: boolean) => void;
}) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="toggle">
      <button
        id={fieldId}
        type="button"
        role="switch"
        aria-checked={toggled}
        disabled={disabled ?? false}
        className="toggle__track"
        onClick={() => onToggle?.(!toggled)}
      >
        <span className="toggle__thumb" />
      </button>
      <label className="toggle__label" htmlFor={fieldId}>
        {labelText}
      </label>
    </div>
  );
}

/** A bare labelled wrapper, for composing controls the set doesn't cover. */
export function Field({
  id,
  labelText,
  helperText,
  invalid,
  invalidText,
  children,
}: Omit<FieldShellProps, 'hideLabel' | 'className'> & { invalid?: boolean }) {
  return (
    <FieldShell
      id={id}
      labelText={labelText}
      helperText={helperText}
      invalid={invalid ?? false}
      invalidText={invalidText}
    >
      {children}
    </FieldShell>
  );
}
